/**
 * Postausgang (DEV-93): every queued, sending or failed customer notification,
 * across all product versions, with the steps of the manual transport.
 *
 * The mail is HTML. `mailto:` cannot carry HTML, so "Im Mailprogramm öffnen"
 * opens a mail with recipient and subject and puts the formatted content on
 * the clipboard (HTML plus a plain-text fallback) to paste.
 *
 * Opening the mail in the mail client (or copying its content) BEGINS sending:
 * from then on the notification can no longer be withdrawn — only confirmed
 * as sent or released back into the queue. The server decides every step
 * under one lock; a 409 means someone else was faster and the list reloads.
 *
 * Sent mails are hidden by default; "Versendete anzeigen" lists them after the
 * queue (newest first) as the record of what went out. The search covers CVE,
 * product, customer, recipient and subject.
 */

import type { MessageDescriptor } from '@lingui/core';
// Core `t` is aliased: the components below take their own `t` from useLingui().
import { msg, plural, t as translate } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useMemo, useState, type ReactNode } from 'react';

import Banner from '../../components/Banner';
import Chip, { ChipList } from '../../components/Chip';
import CveLink from '../../components/CveLink';
import EmptyState from '../../components/EmptyState';
import Icon from '../../components/Icon';
import Spinner from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { errorMessage, outboxAction } from '../../lib/api';
import { fmtDate, fmtValue } from '../../lib/format';
import { missingPermissionTitle } from '../../lib/permissions';
import type { OutboxAction, OutboxRow } from '../../lib/types';

import { isNotificationSent, NOTIFICATION_STATUS_LABELS, notificationTone } from './eventHelpers';
import MailPreview from './MailPreview';
import { markdownToHtml, markdownToPlainText } from './mailText';

export interface OutboxPanelProps {
  /** Queued, sending and failed notifications, plus the sent ones (hidden by default). */
  rows: readonly OutboxRow[];
  /** `notification.send` — without it the Postausgang is read-only. */
  canSend: boolean;
  /** Reload rows and counters after every step. */
  onChanged: () => void;
}

const DONE: Readonly<Record<OutboxAction, MessageDescriptor>> = {
  withdraw: msg`Zurückgezogen — der Entwurf ist wieder bearbeitbar.`,
  begin: msg`Versand begonnen.`,
  release: msg`Freigegeben — wieder im Postausgang.`,
  confirm: msg`Als versendet bestätigt.`,
  override: msg`Zum Versand freigegeben.`,
  requeue: msg`Erneut eingereiht.`,
};

/** Recipient and subject only: a `mailto:` body cannot carry HTML. */
function mailtoOf(row: OutboxRow): string | null {
  if (!row.empfaenger) return null;
  return `mailto:${encodeURIComponent(row.empfaenger)}?subject=${encodeURIComponent(row.betreff)}`;
}

/**
 * The mail on the clipboard in both formats: mail clients paste the HTML,
 * plain-text targets get the text. Starts within the click (the clipboard
 * needs the user gesture). Mails queued before HTML mails fall back to their text.
 */
function copyMail(row: OutboxRow): Promise<void> {
  const html = row.html ?? markdownToHtml(row.inhalt);
  const plain = markdownToPlainText(row.inhalt);
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
    return navigator.clipboard.write([new ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([plain], { type: 'text/plain' }),
    })]);
  }
  return navigator.clipboard.writeText(plain);
}

/** Free-text search over what identifies a mail. */
function matches(row: OutboxRow, needle: string): boolean {
  return [row.cve, row.product_name, row.version_nr, row.kunde.name, row.empfaenger, row.betreff]
    .some((value) => value?.toLowerCase().includes(needle));
}

function productLabel(row: OutboxRow): string {
  const caseId = row.case_id ?? '?';
  return [row.product_name, row.version_nr].filter(Boolean).join(' ') || translate`Schwachstellenfall #${caseId}`;
}

/** Hints that need a decision before or after sending. */
function OutboxHints({ row }: Readonly<{ row: OutboxRow }>): ReactNode {
  const { t } = useLingui();
  if (isNotificationSent(row.status)) return null;
  const lastError = row.last_error;
  const attempts = row.send_attempts;
  const nextAttempt = row.next_attempt_at ? fmtDate(row.next_attempt_at) : null;
  return (
    <>
      {row.contradiction && !row.override ? (
        <Banner kind="warn" sub={t`Zurückziehen oder bewusst trotzdem senden.`}>
          <Trans>Keine Version des Produkts ist mehr als „Betroffen“ bestätigt — diese Mail wird nicht versendet.</Trans>
        </Banner>
      ) : null}
      {row.advisory_changed ? (
        <Banner kind="info" sub={t`Gesendet wird der eingefrorene Text. Für den neuen Stand zurückziehen und neu einreihen.`}>
          <Trans>Advisory seit dem Einreihen geändert.</Trans>
        </Banner>
      ) : null}
      {row.status === 'FAILED' && lastError ? <Banner kind="error"><Trans>Versand fehlgeschlagen: {lastError}</Trans></Banner> : null}
      {row.status === 'QUEUED' && lastError ? (
        <Banner kind="warn" sub={nextAttempt ? t`Nächster Versuch ${nextAttempt}.` : undefined}>
          <Trans>Versuch {attempts} fehlgeschlagen: {lastError}</Trans>
        </Banner>
      ) : null}
    </>
  );
}

interface ActionsProps {
  row: OutboxRow;
  canSend: boolean;
  busy: boolean;
  run: (action: OutboxAction, after?: () => void) => void;
}

/** The steps the manual transport offers in the row's state. */
function OutboxActions({ row, canSend, busy, run }: Readonly<ActionsProps>): ReactNode {
  const { t } = useLingui();
  const toast = useToast();
  const mailto = mailtoOf(row);
  const disabled = !canSend || busy;
  const title = canSend ? undefined : missingPermissionTitle('notification.send');
  // Copying or opening begins sending — unless that already happened. The
  // clipboard is written first, while the click still counts as a user gesture.
  const copyThen = (message: string, after?: () => void): void => {
    void copyMail(row).then(
      () => toast.success(message),
      () => toast.error(t`Kopieren nicht möglich — bitte den Inhalt aus der Vorschau kopieren.`),
    );
    if (row.status === 'SENDING') after?.();
    else run('begin', after);
  };
  const openMail = (): void => { if (mailto) window.location.href = mailto; };
  const blocked = row.contradiction && !row.override;

  if (row.status === 'FAILED') {
    return (
      <>
        <button type="button" className="ghost small" disabled={disabled} title={title} onClick={() => run('withdraw')}><Trans>Zurückziehen</Trans></button>
        <button type="button" className="primary small" disabled={disabled} title={title} onClick={() => run('requeue')}><Trans>Erneut einreihen</Trans></button>
      </>
    );
  }
  return (
    <>
      {row.status === 'QUEUED' ? (
        <button type="button" className="ghost small" disabled={disabled} title={title} onClick={() => run('withdraw')}><Trans>Zurückziehen</Trans></button>
      ) : (
        <button type="button" className="ghost small" disabled={disabled} title={title}
          onClick={() => run('release')}><Trans>Freigeben</Trans></button>
      )}
      {blocked ? (
        <button type="button" className="ghost small" disabled={disabled} title={title} onClick={() => run('override')}><Trans>Trotzdem senden</Trans></button>
      ) : (
        <>
          <button type="button" className="ghost small" disabled={disabled} title={title}
            onClick={() => copyThen(t`Inhalt formatiert kopiert.`)}><Trans>Inhalt kopieren</Trans></button>
          <button type="button" className="ghost small" disabled={disabled || mailto === null}
            title={mailto === null ? t`Keine Kontakt-E-Mail hinterlegt` : t`Öffnet die Mail mit Empfänger und Betreff; der Inhalt liegt zum Einfügen in der Zwischenablage`}
            onClick={() => copyThen(t`Inhalt kopiert — im Mailprogramm in die neue Mail einfügen.`, openMail)}>
            <Icon name="mail" /> <Trans>Im Mailprogramm öffnen</Trans>
          </button>
        </>
      )}
      {row.status === 'SENDING' ? (
        <button type="button" className="primary small" disabled={disabled} title={title}
          onClick={() => {
            const customer = fmtValue(row.kunde.name);
            if (window.confirm(t`Wurde die Mail an ${customer} versendet?`)) run('confirm');
          }}>
          <Trans>Versand bestätigen</Trans>
        </button>
      ) : null}
    </>
  );
}

/** The status with its moment: when it went out, since when it is being sent. */
function statusLabel(row: OutboxRow): string {
  const label = NOTIFICATION_STATUS_LABELS[row.status] ?? row.status;
  if (isNotificationSent(row.status)) {
    const sentAt = fmtDate(row.versendet_am);
    return translate`${label} ${sentAt}`;
  }
  if (row.status === 'SENDING') {
    const startedAt = fmtDate(row.send_started_at);
    return translate`${label} seit ${startedAt}`;
  }
  return label;
}

/** Recipient, record number, scope and who did what — one quiet line. */
function metaLine(row: OutboxRow): string {
  const transport = row.transport_label ?? row.transport;
  const recipient = row.empfaenger ?? translate`(keine E-Mail)`;
  const { id, queued_by: queuedBy, send_started_by: startedBy, status_changed_by: confirmedBy } = row;
  const queuedAt = row.queued_at ? fmtDate(row.queued_at) : null;
  let queued: string | null = null;
  if (queuedAt) queued = queuedBy ? translate`eingereiht ${queuedAt} von ${queuedBy}` : translate`eingereiht ${queuedAt}`;
  let started: string | null = null;
  if (row.status === 'SENDING') started = startedBy ? translate`Versand begonnen von ${startedBy}` : translate`Versand begonnen`;
  let confirmed: string | null = null;
  if (isNotificationSent(row.status)) confirmed = confirmedBy ? translate`bestätigt von ${confirmedBy}` : translate`bestätigt`;
  const parts = [
    translate`An ${recipient}`,
    translate`Benachrichtigung #${id}`,
    translate`${plural(row.instance_count, { one: '# Anlage', other: '# Anlagen' })}`,
    queued,
    started,
    confirmed,
    transport ? translate`Transport ${transport}` : null,
  ];
  return parts.filter(Boolean).join(' · ');
}

function OutboxItem({ row, canSend, busy, run }: Readonly<ActionsProps>): ReactNode {
  const { t } = useLingui();
  const sent = isNotificationSent(row.status);
  const previousId = row.previous_id;
  const customer = fmtValue(row.kunde.name);
  return (
    <article className="mail outbox-item">
      <div className="outbox-head">
        <strong>{fmtValue(row.kunde.name)}</strong>
        <span>{productLabel(row)}</span>
        {row.cve ? <CveLink cve={row.cve} /> : null}
      </div>
      <ChipList>
        <Chip tone={notificationTone(row.status)}>{statusLabel(row)}</Chip>
        {previousId === null ? null : <Chip tone="info"><Trans>Nachtrag zu #{previousId}</Trans></Chip>}
        {row.transport_simulated ? (
          <Chip title={t`Demo-Transport: nichts wurde verschickt — kein Versandnachweis`}><Trans>simuliert</Trans></Chip>
        ) : null}
      </ChipList>
      <div className="outbox-meta" title={row.message_id ?? undefined}>{metaLine(row)}</div>
      <OutboxHints row={row} />
      <details>
        <summary>{row.betreff || t`(ohne Betreff)`}</summary>
        {row.html ? <MailPreview html={row.html} title={t`Mail an ${customer}`} /> : <pre>{row.inhalt}</pre>}
      </details>
      {sent ? null : (
        <div className="triage-mail-foot">
          <div className="spacer" />
          <OutboxActions row={row} canSend={canSend} busy={busy} run={run} />
        </div>
      )}
    </article>
  );
}

export function OutboxPanel({ rows, canSend, onChanged }: Readonly<OutboxPanelProps>): ReactNode {
  const { t, i18n } = useLingui();
  const toast = useToast();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [showSent, setShowSent] = useState(false);
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => (showSent || !isNotificationSent(row.status)) && (!needle || matches(row, needle)));
  }, [rows, search, showSent]);
  // Nothing to show at all (as opposed to a search without hits).
  const empty = rows.length === 0 || (!showSent && rows.every((row) => isNotificationSent(row.status)));

  const runFor = (row: OutboxRow) => (action: OutboxAction, after?: () => void): void => {
    setBusyId(row.id);
    outboxAction(row.id, action).then(
      () => {
        after?.();
        if (action !== 'begin' || !after) toast.success(i18n._(DONE[action]));
      },
      (err: unknown) => toast.error(errorMessage(err)),
    ).finally(() => {
      setBusyId(null);
      onChanged();
    });
  };

  return (
    <section className="card">
      <div className="card-head table-head">
        <h2><Trans>Postausgang</Trans> {busyId === null ? null : <Spinner />}</h2>
      </div>
      <div className="table-toolbar">
        <input type="search" value={search} placeholder={t`CVE, Produkt, Kunde, Empfänger oder Betreff suchen…`}
          aria-label={t`Postausgang filtern`} onChange={(e) => setSearch(e.target.value)} />
        <label className="check">
          <input type="checkbox" checked={showSent} onChange={(e) => setShowSent(e.target.checked)} />
          <span><Trans>Versendete anzeigen</Trans></span>
        </label>
        <div className="table-toolbar-options">
          <span className="table-count" aria-live="polite">{t`${plural(visible.length, { one: '# Mail', other: '# Mails' })}`}</span>
        </div>
      </div>
      {visible.length === 0 ? (
        <EmptyState title={empty ? t`Postausgang leer` : t`Keine Treffer`}>
          {empty
            ? t`Eingereiht wird im Reiter „Entwürfe & Versand“ einer Benachrichtigung.`
            : t`Suche anpassen oder „Versendete anzeigen“ wählen.`}
        </EmptyState>
      ) : (
        <div className="outbox-list">
          {visible.map((row) => (
            <OutboxItem key={row.id} row={row} canSend={canSend} busy={busyId !== null} run={runFor(row)} />
          ))}
        </div>
      )}
    </section>
  );
}

export default OutboxPanel;
